import type { LocaleCode } from './i18n'

export type TableShape = 'round' | 'square' | 'rectangle' | 'sweetheart'
export type TableColor = 'blue' | 'pink' | 'orange'
export type Rsvp = 'pending' | 'yes' | 'no'

/**
 * What the plan is for. Weddings came first and keep their own words — the
 * sweetheart table, "Bride & Groom" — and every other kind gets a head table
 * and plainer ones. It decides the room a plan starts with and a few labels;
 * the canvas, the merge and the PDF layout never ask.
 */
export type EventKind = 'wedding' | 'celebration' | 'corporate' | 'other'

/**
 * How a chair is drawn — on the canvas and, identically, in the PDF.
 *
 * Occupancy is fill colour in all three; what changes is how much of the guest
 * comes with it. Only `name` is drawn as an outward plate, because only a whole
 * name needs one.
 */
export type SeatLabelMode = 'name' | 'initials' | 'none'

/** What a *new* table of each shape is born with. */
export type DefaultSeats = Record<TableShape, number>

export interface PlanSettings {
  defaultSeats: DefaultSeats
  defaultTableColor: TableColor
  seatLabels: SeatLabelMode
}

export interface Seat {
  id: string
  position: number
  guestId: string | null
  label: string | null
}

export interface Table {
  id: string
  name: string
  shape: TableShape
  color: TableColor
  x: number
  y: number
  /** Degrees clockwise. */
  rotation: number
  seatCount: number
  zIndex: number
  seats: Seat[]
}

export interface Guest {
  id: string
  fullName: string
  groupLabel: string | null
  note: string | null
  rsvp: Rsvp
  photoAssetId: string | null
  photoUrl: string | null
}

export interface Viewport {
  x: number
  y: number
  zoom: number
}

export interface PlanSummary {
  id: string
  title: string
  /** Part of the document: merged, undone and synced like the date and venue. */
  eventKind: EventKind
  eventDate: string | null
  venue: string | null
  /** Drives both the editor's chrome and the language the PDF prints in. */
  language: LocaleCode
  version: number
  createdAt: string
  updatedAt: string
  tableCount: number
  guestCount: number
  seatedCount: number
  seatCount: number
}

export interface Plan extends PlanSummary {
  viewport: Viewport
  settings: PlanSettings
  tables: Table[]
  guests: Guest[]
}

export interface UploadedAsset {
  id: string
  url: string
  width: number
  height: number
}

export interface ExportStatus {
  exportable: boolean
  tables: number
  seatedGuests: number
  /**
   * Pages the floor plan spans if the room decides (1, 2 or 4). The export
   * dialog preselects this and lets the planner overrule it per download.
   */
  floorPlanPages: number
  /** The plan's own chair style, which the export dialog starts from. */
  seatLabels: SeatLabelMode
  /**
   * What each chair style would cost in paper. Name plates need more room than
   * discs, so the dialog can say what a style costs *before* it is picked.
   */
  floorPlanPagesBySeatLabels: Record<SeatLabelMode, number>
}

/**
 * One table read off a photographed venue chart.
 *
 * A proposal, not a table: it carries no id and no chairs, because the editor
 * mints those itself when the import is accepted. That keeps every write on the
 * one save path — client-generated ids, whole-canvas PUT, plain undo.
 */
export interface DetectedTable {
  name: string
  shape: TableShape
  color: TableColor
  x: number
  y: number
  rotation: number
  seatCount: number
  /** True when the chair count came off the drawing rather than a default. */
  chairsCounted: boolean
}

export interface FloorPlanDetection {
  tables: DetectedTable[]
  tableCount: number
  roundCount: number
  squareCount: number
  rectCount: number
  seatTotal: number
  chairsCounted: number
  /** Drawn shapes that did not repeat — room labels, stage, kitchen. */
  ignored: number
  rectified: boolean
  truncated: boolean
  /** data: URI of the annotated sheet. */
  previewImage: string
}

/**
 * A failure with a stable `code`. Storage raises these exactly where the server
 * used to answer with `{"error": {"code", ...}}`, so the UI keeps branching on
 * `code` and rendering the words from the catalogue (`messageForError`).
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  get isConflict(): boolean {
    return this.status === 409
  }
}
